import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  acceptInvitation,
  changeMemberRole,
  createInvitation,
  listInvitations,
  listMembers,
  removeMember,
  revokeInvitation,
} from './api'
import type { AcceptedInvitation, CreatedInvitation } from './types'

export function teamMembersQueryKey(tenantId: string) {
  return ['team', 'members', tenantId] as const
}

export function teamInvitationsQueryKey(tenantId: string) {
  return ['team', 'invitations', tenantId] as const
}

export function useTeamMembers(tenantId: string | undefined) {
  return useQuery({
    queryKey: teamMembersQueryKey(tenantId ?? ''),
    queryFn: () => listMembers(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

export function useTeamInvitations(tenantId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: teamInvitationsQueryKey(tenantId ?? ''),
    queryFn: () => listInvitations(tenantId ?? ''),
    enabled: tenantId !== undefined && enabled,
  })
}

export function useChangeMemberRole(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { membershipId: string; role: string }) =>
      changeMemberRole(tenantId, variables.membershipId, variables.role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamMembersQueryKey(tenantId) })
    },
  })
}

export function useRemoveMember(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { membershipId: string }) =>
      removeMember(tenantId, variables.membershipId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamMembersQueryKey(tenantId) })
    },
  })
}

export function useCreateInvitation(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<CreatedInvitation, unknown, { email: string; role: string }>({
    mutationFn: (variables) => createInvitation(tenantId, variables.email, variables.role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamInvitationsQueryKey(tenantId) })
    },
  })
}

export function useRevokeInvitation(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: { invitationId: string }) =>
      revokeInvitation(tenantId, variables.invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamInvitationsQueryKey(tenantId) })
    },
  })
}

export function useAcceptInvitation() {
  return useMutation<AcceptedInvitation, unknown, { token: string }>({
    mutationFn: (variables) => acceptInvitation(variables.token),
  })
}
